import {EditorContainerBox, FormLoadingButton} from "./styled";
import AddIcon from "@mui/icons-material/Add";
import React, {useCallback, useMemo, useState} from "react";
import {useTranslation} from 'react-i18next';
import CircularProgress from "@mui/material/CircularProgress";
import {UserRoles} from "constants/userRoles";
import {useGetTags} from "hooks/useGetTags";
import {useLessonFormValidator} from "hooks/useLessonFormValidator";
import {useAddLesson} from "hooks/useAddLesson";
import {convertFileToBase64} from "helpers/fileHelper";
import {useNavigate} from "react-router-dom";
import {RoutePaths} from "constants/RoutePaths";
import AuthGuard from "context/auth/AuthGuard";
import {IUpdateLessonNodesRequest} from "api/models/request/IUpdateLessonNodesRequest";
import {useUpdateLessonNodes} from "hooks/useUpdateLessonNodes";
import {LessonFormDrawer} from "components/views/editor/components/LessonFormDrawer";
import {LessonForm} from "components/views/editor/models/LessonForm";
import {NodeEditor} from "components/views/editor/components/NodeEditor";
import {useNodeEditorValidator} from "hooks/useNodeEditorValidator";
import {LessonFormFields} from "constants/lessonFormFields";
import {ConfirmNavigationDialog} from "components/views/editor/components/ConfirmNavigationDialog";
import {useNavigationBlocker} from "hooks/useNavigationBlocker";
import { LoadingBox, NodeEditorContentBox, ActionButtonsBox } from "./AddTrainingPage.styles";

export const AddTrainingPage = () => {

    const initialValues: LessonForm = {
        description: null,
        image: null,
        name: "",
        photoExtension: null,
        tagA: null,
        tagB: null,
        maxPoints: 0
    }

    const [lessonFormData, setLessonFormData] = useState<LessonForm>(initialValues);
    const [editor, setEditor] = useState<any>();
    const {tags, isLoading: getTagsIsLoading} = useGetTags();
    const {addLesson, addingLesson: addLessonPending} = useAddLesson();
    const {updateLessonNodes, isLoading: updateLessonNodesPending} = useUpdateLessonNodes();
    const {isValid: isLessonFormValid, reset: resetFormErrors, errors: formValidationErrors} = useLessonFormValidator();
    const {isValid: isNodeEditorValid} = useNodeEditorValidator();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const hasDataChanged = useMemo(() => () =>
            Object.keys(editor.toCustomJSON().jsonConfig.nodes).length > 0,
        [editor]);
    const {blocker, setBlockerActive} = useNavigationBlocker(hasDataChanged);

    const handleFormDataChange = useCallback((lessonFormField: LessonFormFields, newLessonFormData: LessonForm) => {
        setLessonFormData(newLessonFormData);
        if (Object.keys(formValidationErrors).length > 0) resetFormErrors([lessonFormField]);
    },[formValidationErrors, resetFormErrors]);

    const onEditorChange = useCallback((editor: object) => {
        setEditor(editor);
    },[]);

    const handleAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!isLessonFormValid(lessonFormData)) return;

        if (editor && editor.nodes.length > 0 && !isNodeEditorValid(editor)) return;

        let base64WithoutPrefix = null;
        let extension = null;
        if (lessonFormData.image) {
            const base64String = await convertFileToBase64(lessonFormData.image.file);
            base64WithoutPrefix = (base64String as string).split(',')[1];
            extension = lessonFormData.image.file.name.split('.').pop() ?? null;
        }

        const addLessonResponse = await addLesson({
            lessonName: lessonFormData.name,
            lessonDescription: lessonFormData.description,
            tagA: lessonFormData.tagA,
            tagB: lessonFormData.tagB,
            maxPoints: lessonFormData.maxPoints,
            base64Photo: base64WithoutPrefix,
            pictureExtension: extension
        });

        const newLessonId = addLessonResponse?.success && addLessonResponse.data ? addLessonResponse.data : null;
        if (editor && editor.nodes.length > 0 && newLessonId) {
            const updateLessonNodesRequest: IUpdateLessonNodesRequest = editor.toCustomJSON();
            updateLessonNodesRequest.lessonId = newLessonId;

            await updateLessonNodes(updateLessonNodesRequest);
        }

        if (newLessonId) {
            editor.storage.saveTempSelectedGroup(newLessonId);
            setBlockerActive(false);
            navigate(`${RoutePaths.EDITOR}/${newLessonId}`);
        }
    };

    const handleCancelDialog = () => { blocker.reset?.(); }

    const handleConfirmDialog = () => { blocker.proceed?.(); }

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron]}>
            <EditorContainerBox>
                {!tags || getTagsIsLoading || addLessonPending ? (
                    <LoadingBox>
                        <CircularProgress color="primary"/>
                    </LoadingBox>
                ) : (
                    <NodeEditorContentBox id="node-editor-content">
                        <LessonFormDrawer
                            tags={tags}
                            isDefaultOpen
                            lessonFormData={lessonFormData}
                            formValidationErrors={formValidationErrors}
                            onLessonDataChange={handleFormDataChange}/>
                        <ActionButtonsBox>
                            <FormLoadingButton
                                variant="contained"
                                loading={addLessonPending || updateLessonNodesPending}
                                onClick={handleAddClick}
                                startIcon={<AddIcon/>}
                            >
                                {t("lessonEditor.add")}
                            </FormLoadingButton>
                        </ActionButtonsBox>
                        <NodeEditor onEditorChange={onEditorChange}/>
                    </NodeEditorContentBox>
                )}
                <ConfirmNavigationDialog
                    open={blocker.state === "blocked"}
                    handleCancel={handleCancelDialog}
                    handleConfirm={handleConfirmDialog}
                    message={t("nodeEditor.unsavedChanges")}
                />
            </EditorContainerBox>
        </AuthGuard>
    );
}